"use client"

import { useState, useEffect } from "react"
import ModernSidebar from "./admin_sidebar"
import ModernHeader from "./modern-header"
import axios from "axios"
import { Linkedin, Users, ChevronDown, ChevronUp, UserPlus, Check, Loader2, X } from 'lucide-react'
import toast, { Toaster } from 'react-hot-toast';
import {
    createMentorRequest,
    getRequestsByStartup,
    getAllMentors,
    getStartupProfile,
    getAllMentorsWithoutAuthenticated,
} from "../utils/api"
import { isGuest } from "../utils/isGuest"
import { useNavigate } from "react-router-dom"

export default function AdminMentor() {
    const [sidebarOpen, setSidebarOpen] = useState(false)
    const [isMobile, setIsMobile] = useState(false)
    const [fullName, setFullName] = useState("")
    const [email, setEmail] = useState("")
    const [mentors, setMentors] = useState([])
    const [loading, setLoading] = useState(true)
    const [expandedMentors, setExpandedMentors] = useState({})
    const [requestingMentors, setRequestingMentors] = useState({})
    const [selectedMentor, setSelectedMentor] = useState(null)
    const [messageModalOpen, setMessageModalOpen] = useState(false)
    const [requestMessage, setRequestMessage] = useState("")
    const [isSendingRequest, setIsSendingRequest] = useState(false)
    const navigate = useNavigate()

    const toggleSidebar = () => setSidebarOpen((prev) => !prev)

    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth < 768
            setIsMobile(mobile)
            if (mobile) setSidebarOpen(false)
        }

        handleResize()
        window.addEventListener("resize", handleResize)
        window.addEventListener("orientationchange", () => {
            setTimeout(handleResize, 100)
        })

        return () => {
            window.removeEventListener("resize", handleResize)
            window.removeEventListener("orientationchange", handleResize)
        }
    }, [])

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const startupId = localStorage.getItem("startupId");
                const isGuestUser = isGuest(); // Your function to detect guest

                // Choose API based on user type
                const getMentors = isGuestUser ? getAllMentorsWithoutAuthenticated : getAllMentors;

                const [profileResRaw, mentorsResRaw, requestsResRaw] = await Promise.all([
                    // Only fetch profile and requests if not guest
                    isGuestUser ? Promise.resolve({}) : getStartupProfile(startupId),
                    getMentors(),
                    isGuestUser ? Promise.resolve([]) : getRequestsByStartup(startupId),
                ]);

                // Normalize profile
                const profile = profileResRaw?.data ?? profileResRaw ?? {};

                // Normalize mentors -> produce an array
                const mentorsData = Array.isArray(mentorsResRaw)
                    ? mentorsResRaw
                    : mentorsResRaw?.data ?? mentorsResRaw?.data?.data ?? [];

                // Normalize requests -> produce an array
                const requestsData = Array.isArray(requestsResRaw)
                    ? requestsResRaw
                    : requestsResRaw?.data ?? requestsResRaw?.data?.data ?? [];

                // Set profile fields safely
                setFullName(
                    profile.fullName ?? profile.name ?? localStorage.getItem("startupName") ?? ""
                );
                setEmail(profile.email ?? "");

                // Filter only active mentors
                const activeMentors = mentorsData.filter(
                    (m) => m && (m.isActive === true || Number(m.isActive) === 1 || String(m.isActive) === "1")
                );

                // Extract requested mentor ids
                const requestedMentorIds = (requestsData || [])
                    .map((r) => r?.mentor_id?._id ?? r?.mentor_id ?? r?.mentorId)
                    .filter(Boolean);

                // Build final mentors list with `requested` boolean
                setMentors(
                    activeMentors.map((mentor) => ({
                        ...mentor,
                        requested: requestedMentorIds.includes(mentor._id),
                    }))
                );
            } catch (err) {
                console.error("Error fetching data:", err);
                toast.error("❌ Failed to load data");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);


    const openMessageModal = (mentor) => {
        setSelectedMentor(mentor);
        setRequestMessage("");
        setMessageModalOpen(true);
    };

    const closeMessageModal = () => {
        setMessageModalOpen(false);
        setSelectedMentor(null);
        setRequestMessage("");
        setIsSendingRequest(false);
    };

    const handleRequestMentor = async () => {
        if (!selectedMentor) return;

        const mentorId = selectedMentor._id;

        // Prevent multiple clicks on the same button
        if (requestingMentors[mentorId] || isSendingRequest) {
            return;
        }

        try {
            setIsSendingRequest(true);
            // Set requesting state to prevent multiple clicks
            setRequestingMentors(prev => ({ ...prev, [mentorId]: true }));

            const startupId = localStorage.getItem("startupId")
            const startupName = localStorage.getItem("startupName")

            if (!startupId || !startupName || !fullName) {
                toast.error("❌ Missing required information")
                return
            }

            setMentors(prev => prev.map(m =>
                m._id === mentorId ? { ...m, isRequesting: true } : m
            ))

            const res = await createMentorRequest({
                mentorId,
                startupId,
                startupName,
                founderName: fullName,
                message: requestMessage 
            })

            if (res.alreadyRequested) {
                toast(res.message, {
                    icon: '⚠️',
                    style: {
                        background: '#FEF3C7', 
                        color: '#92400E',      
                        border: '1px solid #F59E0B', 
                    },
                });

            } else {
                toast.success(res.message); 
            }
            setMentors(prev => prev.map(m =>
                m._id === mentorId ? {
                    ...m,
                    requested: true,
                    isRequesting: false,
                    requestId: res.data._id
                } : m
            ))

            const requestedMentors = JSON.parse(localStorage.getItem("requestedMentors") || "{}")
            requestedMentors[mentorId] = true
            localStorage.setItem("requestedMentors", JSON.stringify(requestedMentors))

            closeMessageModal();

        } catch (err) {
            console.error("Mentor request error:", err)

            // Check if it's a transaction error that might succeed on retry
            const errorMessage = err.response?.data?.message || err.message || 'Failed to send request';

            if (errorMessage.includes("transaction") && errorMessage.includes("does not match")) {
                // This is likely a transient transaction error
                // Check if the request actually succeeded despite the error
                try {
                    // Verify if the request was actually created
                    const startupId = localStorage.getItem("startupId");
                    const requestsRes = await getRequestsByStartup(startupId);

                    const requestsData = Array.isArray(requestsRes)
                        ? requestsRes
                        : requestsRes?.data ?? requestsRes?.data?.data ?? [];

                    const requestedMentorIds = requestsData.map((r) => {
                        return r?.mentor_id?._id ?? r?.mentor_id ?? r?.mentorId ?? null;
                    }).filter(Boolean);

                    if (requestedMentorIds.includes(mentorId)) {
                        toast.success("✅ Mentorship request sent successfully!");
                        setMentors(prev => prev.map(m =>
                            m._id === mentorId ? { ...m, requested: true, isRequesting: false } : m
                        ));
                        closeMessageModal();
                        return;
                    }
                } catch (verifyError) {
                    console.error("Error verifying request:", verifyError);
                }

                toast.error("⚠️ Please try again. Temporary server issue.");
            } else {
                toast.error(`❌ ${errorMessage}`);
            }

            setMentors(prev => prev.map(m =>
                m._id === mentorId ? { ...m, isRequesting: false } : m
            ))

        } finally {
            setIsSendingRequest(false);
            // Clear the requesting state after a delay to prevent rapid re-clicks
            setTimeout(() => {
                setRequestingMentors(prev => ({ ...prev, [mentorId]: false }));
            }, 1000);
        }
    }

    const toggleExpertiseExpansion = (mentorId) => {
        setExpandedMentors(prev => ({
            ...prev,
            [mentorId]: !prev[mentorId]
        }));
    };

    return (
        <div className="flex h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/30 overflow-hidden">
            {/* Sidebar */}
            <ModernSidebar
                isOpen={sidebarOpen}
                toggleSidebar={toggleSidebar}
                isMobile={isMobile}
                fullName={fullName}
                email={email}
            />

            <Toaster position="top-right" reverseOrder={false} />

            {/* Message Modal */}
            {messageModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-xl font-bold text-gray-900">Send Mentorship Request</h3>
                            <button
                                onClick={closeMessageModal}
                                className="text-gray-500 hover:text-gray-700"
                                disabled={isSendingRequest}
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        <div className="mb-6">
                            <p className="text-gray-600 mb-2">
                                You're requesting mentorship from <span className="font-semibold">{selectedMentor?.name}</span>.
                                Add a personal message to introduce yourself and explain why you'd like to connect:
                            </p>

                            <textarea
                                value={requestMessage}
                                onChange={(e) => setRequestMessage(e.target.value)}
                                placeholder="Hi, I'd like to connect because..."
                                className="w-full h-32 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
                                disabled={isSendingRequest}
                            />

                            <p className="text-sm text-gray-500 mt-1">
                                This message will be included in the email sent to the mentor.
                            </p>
                        </div>

                        <div className="flex justify-end gap-3">
                            <button
                                onClick={closeMessageModal}
                                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                                disabled={isSendingRequest}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleRequestMentor}
                                disabled={isSendingRequest || requestingMentors[selectedMentor?._id]}
                                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isSendingRequest ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Sending...
                                    </>
                                ) : (
                                    "Send Request"
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Main Content */}
            <div
                className={`flex flex-col flex-1 transition-all duration-300 min-h-0 ${!isMobile ? "ml-16 sm:ml-18 md:ml-20 lg:ml-20 xl:ml-20" : "ml-0"
                    }`}
            >
                {/* Header */}
                <ModernHeader toggleSidebar={toggleSidebar} isMobile={isMobile} />

                {/* Page Content */}
                <div className="flex-1 p-4 xs:p-6 sm:p-8 overflow-y-auto min-h-0">
                    {/* Page Header */}
                    <div className="mb-8">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="w-8 h-8 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
                                <Users className="w-4 h-4 text-white" />
                            </div>
                            <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                                Available Mentors
                            </h1>
                        </div>
                        <p className="text-gray-600 text-lg">Connect with industry experts to accelerate your startup journey</p>
                    </div>

                    {loading ? (
                        // Skeleton Loader
                        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
                            {[...Array(6)].map((_, idx) => (
                                <div key={idx} className="group relative">
                                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 animate-pulse hover:shadow-lg transition-all duration-300">
                                        <div className="relative mb-6">
                                            <div className="w-20 h-20 rounded-full bg-gradient-to-r from-gray-200 to-gray-300 mx-auto"></div>
                                            <div className="absolute -bottom-2 -right-2 w-6 h-6 bg-gray-200 rounded-full"></div>
                                        </div>
                                        <div className="space-y-3 mb-6">
                                            <div className="h-5 bg-gray-200 rounded-lg w-3/4 mx-auto"></div>
                                            <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto"></div>
                                            <div className="h-3 bg-gray-200 rounded w-2/3 mx-auto"></div>
                                        </div>
                                        <div className="flex flex-wrap gap-2 justify-center mb-6">
                                            <div className="h-6 bg-gray-200 rounded-full w-16"></div>
                                            <div className="h-6 bg-gray-200 rounded-full w-20"></div>
                                        </div>
                                        <div className="h-11 bg-gray-200 rounded-xl w-full"></div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : mentors.length === 0 ? (
                        <div className="text-center py-16">
                            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                                <Users className="w-12 h-12 text-gray-400" />
                            </div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-2">No mentors available</h3>
                            <p className="text-gray-500 max-w-md mx-auto">
                                We're working on bringing amazing mentors to our platform. Check back soon!
                            </p>
                        </div>
                    ) : (
                        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-2 grid-rows-auto">
                            {mentors.map((mentor) => {
                                const expertiseList = mentor.expertise
                                    ? mentor.expertise.split(',').map(skill => skill.trim())
                                    : ['General Mentoring'];

                                const isExpanded = expandedMentors[mentor._id];
                                const maxVisibleTags = 4; // Number of tags to show before collapsing
                                const hasMoreTags = expertiseList.length > maxVisibleTags;
                                const visibleTags = isExpanded
                                    ? expertiseList
                                    : expertiseList.slice(0, maxVisibleTags);

                                return (
                                    <div
                                        key={mentor._id}
                                        className="group relative bg-white rounded-2xl shadow-sm border border-gray-100 p-8 hover:shadow-xl hover:shadow-indigo-500/10 hover:-translate-y-1 transition-all duration-300 flex flex-col"
                                    >
                                        <div className="absolute inset-0 bg-gradient-to-r from-indigo-500/10 to-purple-500/10 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10"></div>
                                        <div className="flex items-center justify-between mb-4 bg-white p-4 rounded-lg shadow-sm">
                                            {/* Left side: image + details */}
                                            <div className="flex items-center">
                                                <img
                                                    src={mentor.image ? mentor.image : "/placeholder.svg?height=80&width=80&query=professional+headshot"}
                                                    alt={mentor.name}
                                                    className="w-20 h-20 rounded-full object-cover border-4 border-white shadow-lg"
                                                />
                                                <div className="ml-4">
                                                    <h2 className="text-xl font-bold text-gray-900 mb-1">
                                                        {mentor.name}
                                                    </h2>
                                                    <p className="text-indigo-600 font-medium text-sm mb-1">
                                                        {mentor.profession}
                                                    </p>
                                                    <p className="text-gray-500 text-sm">
                                                        {mentor.email}
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Right side: green dot */}
                                            <div className="flex items-center justify-end gap-3">
                                                {/* Request button */}
                                                <button
                                                    onClick={() => {
                                                        if (isGuest()) {
                                                            toast.error("Please login to connect Mentor!")
                                                            navigate('/login')
                                                        } else {
                                                            openMessageModal(mentor)
                                                        }
                                                    }}
                                                    disabled={mentor.isRequesting || requestingMentors[mentor._id]}
                                                    className={`w-12 h-12 flex items-center justify-center rounded-full shadow-lg transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-indigo-500/25
    ${mentor.isRequesting || requestingMentors[mentor._id]
                                                            ? "bg-indigo-400 text-white cursor-wait"
                                                            : "bg-black text-white hover:shadow-xl transform hover:scale-105"
                                                        }`}
                                                >
                                                    {mentor.isRequesting || requestingMentors[mentor._id] ? (
                                                        <Loader2 className="w-6 h-6 animate-spin" />
                                                    ) : (
                                                        <UserPlus className="w-6 h-6" />
                                                    )}
                                                </button>

                                                {/* Green status dot */}
                                                <div className="w-6 h-6 bg-green-500 rounded-full border-2 border-white flex items-center justify-center shadow">
                                                    <div className="w-2 h-2 bg-white rounded-full"></div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="mb-4 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 flex-grow">
                                            {/* Expertise tags with expand/collapse */}
                                            <div className="flex-1">
                                                <div className="flex flex-wrap gap-2 mb-2">
                                                    {visibleTags.map((skill, index) => (
                                                        <span
                                                            key={index}
                                                            className="px-3 py-1 bg-gradient-to-r from-indigo-50 to-purple-50 text-indigo-700 text-xs font-medium rounded-full border border-indigo-100"
                                                        >
                                                            {skill}
                                                        </span>
                                                    ))}
                                                </div>

                                                {hasMoreTags && (
                                                    <button
                                                        onClick={() => toggleExpertiseExpansion(mentor._id)}
                                                        className="flex items-center text-indigo-600 text-xs font-medium hover:text-indigo-800 transition-colors"
                                                    >
                                                        {isExpanded ? (
                                                            <>
                                                                <ChevronUp className="w-3 h-3 mr-1" />
                                                                Show Less
                                                            </>
                                                        ) : (
                                                            <>
                                                                <ChevronDown className="w-3 h-3 mr-1" />
                                                                +{expertiseList.length - maxVisibleTags} More
                                                            </>
                                                        )}
                                                    </button>
                                                )}
                                            </div>

                                            {/* LinkedIn Profile Link */}
                                            <div className="shrink-0 self-start sm:self-center">
                                                {mentor.linkedinProfile && (
                                                    <a
                                                        href={mentor.linkedinProfile}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="inline-flex items-center justify-center gap-2 text-blue-600 hover:text-blue-700 font-medium text-sm transition-colors duration-200 whitespace-nowrap"
                                                    >
                                                        <Linkedin className="w-4 h-4" />
                                                        View LinkedIn
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}